import type { ProjectCuringSummary } from './curing';

export interface GeoJSONFeature {
    id: number;
    type: "Feature";
    geometry: {
        type: "MultiPolygon";
        coordinates: number[][][][];
    };
    properties: {
        name: string;
        [key: string]: any;
    };
}

export interface ProjectBoundary {
    type: "Polygon" | "MultiPolygon";
    coordinates: number[][][] | number[][][][];
}

/** One tile of the project's grid, as the project detail returns it. */
export interface ProjectTileInfo {
    col: number;
    row: number;
    status?: 'pending' | 'ready' | 'failed';
    /** While the project expands, what this tile of the batch is doing
     *  ("Loading buildings", "Waiting in line · 2 tiles ahead"). */
    progress?: string;
    /** When that started (ISO 8601), for a running clock beside it. */
    progress_since?: string | null;
}

/** Readiness of one data layer while a project loads behind the map. */
export type LayerState = 'pending' | 'loading' | 'ready' | 'failed' | 'skipped';

export interface LayerStatusEntry {
    state: LayerState;
    /** When the state was set (ISO 8601). */
    at?: string;
    /** Tiles done / total, for the tiled layers. */
    done?: number;
    total?: number;
    error?: string;
}

/** Layer names the backend reports (api/services/layer_status.py), in display order. */
export const PROJECT_LAYERS = [
    'vegetation', 'weather', 'canopy', 'fire_history', 'assets', 'treatments', 'properties', 'curing',
] as const;
export type ProjectLayer = (typeof PROJECT_LAYERS)[number];

export const PROJECT_LAYER_LABELS: Record<ProjectLayer, string> = {
    vegetation: 'Vegetation',
    weather: 'Weather history',
    canopy: 'Tree canopy',
    fire_history: 'Fire history',
    assets: 'Assets',
    treatments: 'Treatments',
    properties: 'Property boundaries',
    curing: 'Grass curing',
};

export type ProjectLayerStatus = Partial<Record<ProjectLayer, LayerStatusEntry>>;

/** Layers still on their way, in display order. */
export const loadingLayers = (status?: ProjectLayerStatus | null): ProjectLayer[] =>
    PROJECT_LAYERS.filter((layer) => {
        const state = status?.[layer]?.state;
        return state === 'pending' || state === 'loading';
    });

export interface Project {
    id: string;
    name: string;
    boundary: ProjectBoundary | null;
    updated: string;
    username: string;
    description?: string;
    // Async initialization tracking
    status?: 'processing' | 'expanding' | 'refreshing' | 'ready' | 'failed' | 'deleting';
    task_id?: string | null;
    // Tiled-init progress: size grid dim and "X of N tiles" counters.
    init_grid_dim?: number;
    init_tiles_total?: number;
    init_tiles_completed?: number;
    /** Per-layer readiness: the project opens once its vegetation is in and
     *  the rest (weather, canopy, fire history, assets, treatments, property
     *  boundaries, curing) report here while they load behind the map. */
    layer_status?: ProjectLayerStatus;
    /** When the create request made it (ISO 8601); null for old projects. */
    created_at?: string | null;
    // Aggregated counts (from list endpoint annotations)
    strategy_count?: number;
    fire_simulation_count?: number;
    /** Active detection events (listing annotation); drives the "open
     *  incidents" highlight on cards and the listing map. */
    open_incident_count?: number;
    /** Latest grassland-curing estimate (listing annotation); drives the
     *  curing overlay on the listing map and the badge on project cards.
     *  Null until the project's first estimate lands. */
    curing_summary?: ProjectCuringSummary | null;
    kpis: {
        value: number;
        units: string;
        label: string;
        icon: string;
    }[];
    properties: GeoJSONFeature[];
    veg_classes: string[];
    model_classes: string[];
    resolution: number;
    timezone: string;
    grid_size: number;
    map_screenshot_url: string;
    assets_tiles_url?: string;
    tags?: string[];
    tile_width_m?: number | null;
    tile_height_m?: number | null;
    origin_lng?: number | null;
    origin_lat?: number | null;
    tiles?: ProjectTileInfo[];
    top?: number | null;
    bottom?: number | null;
    left?: number | null;
    right?: number | null;
    is_archived?: boolean;
    /** Pinned to the top of the listing for everyone in the tenant. */
    is_pinned?: boolean;
    /** When the pin was set — orders the pinned block newest-first. */
    pinned_at?: string | null;
  /** Explorer folder (tenant-wide, see api/projectFolders.ts); null = top level. */
  folder?: number | null;
  /** Drawn on the listing map (tenant-wide); a hidden folder hides its contents too. */
  map_visible?: boolean;
  /** How its property boundaries are drawn (tenant-wide); null = the default green. */
  property_style?: import("./boundaryStyle").BoundaryStyle | null;
  /** [west, south, east, north] of the boundary in degrees (list endpoint only). */
  bbox?: [number, number, number, number] | null;
    /** FK to ConstructionType applied as the default for building assets
     *  that don't specify their own construction_type. */
    default_construction_type?: number | null;
    /** Urban project: building footprints are cut into the vegetation layer
     *  as "Building" (at init, and when building assets are added later). */
    model_urban_environment?: boolean;
    /** How Tree / Shrub polygons are read as crowns: only the keys this
     *  project overrides (see api/crowns.ts CROWN_SETTING_DEFAULTS); {} =
     *  defaults. Saving a change re-packs the crowns in the background. */
    crown_settings?: Record<string, number>;
    /** Per-kind realtime detection switches (e.g. {hotspot: true}); an absent
     *  key means off. PATCHes merge per-key server-side. */
    /** Hand new detection incidents straight to an AI agent (only shown /
     *  effective while the tenant's agent entitlement is on). */
    auto_agent_incidents?: boolean;
    /** Project-specific standing orders for the incident agent, on top of the
     *  organisation-wide ones (Admin → Incident Agent). */
    agent_instructions?: string;
    /** The strategy that describes the estate as it is (plantations,
     *  firebreaks that exist). The incident agent simulates on it and the
     *  exposure calculation reads its treatments. null = most recently
     *  updated strategy. */
    operational_strategy?: number | null;
    /** Kinds this project may switch on: a data source exists AND the tenant
     *  has opted in. False here disables the settings switch. */
    realtime_available?: Record<string, boolean>;
    /** Whether a data source exists, regardless of tenant opt-in. Lets the UI
     *  distinguish "no connector" from "not enabled for your organisation". */
    realtime_configured?: Record<string, boolean>;
}
